import React from "react";
import { getWowStatic } from "hooks/wow/getStaticData";
import WowTooltip from "../../Tooltips/WowTooltip";
import { TooltipContainer } from "@outplayed/tooltips";
import { ReactComponent as TooltipIcon } from "svg/question-line.svg";
import { findEmbellishmentCarriers, findSlotForItem, SLOT_DISPLAY_NAMES } from "lib/embellishmentHelpers";

// One half of a pair. A named embellishment renders its reagent item
// (embellishment_item_id); an inherently-embellished crafted piece carries no
// named bonus, so the worn item itself (item_id) is the embellishment; null
// means the player ran fewer than two embellishments.
const PairHalf = ({ embellishment, carrier, slotItems }) => {
  if (!embellishment) {
    return (
      <div className="pair-half">
        <div className="pair-half-icon empty" />
        <div className="pair-half-info">
          <div className="pair-half-name none">None</div>
        </div>
      </div>
    );
  }

  const displayItemId = embellishment.embellishment_item_id || embellishment.item_id;
  const staticData = getWowStatic("item", displayItemId);

  // A named embellishment's carrier comes from the recommended builds; an
  // inherently-embellished piece is worn directly, so it only needs its slot.
  const isInherent = !embellishment.embellishment_item_id && embellishment.item_id;
  const carrierStaticData = carrier ? getWowStatic("item", carrier.itemId) : null;
  const slot = isInherent ? findSlotForItem(slotItems, embellishment.item_id) : carrier?.slot;
  const slotName = slot ? SLOT_DISPLAY_NAMES[slot] : null;

  return (
    <div className="pair-half">
      <div className="pair-half-icon">
        {staticData ? (
          <WowTooltip type="item" id={displayItemId}>
            <div>
              <img src={staticData.img} />
            </div>
          </WowTooltip>
        ) : null}
      </div>
      <div className="pair-half-info">
        <div className="pair-half-name">{embellishment.name || staticData?.name}</div>
        {carrierStaticData?.name ? (
          <div className="pair-half-carrier">
            on <span className="pair-half-carrier-name">{carrierStaticData.name}</span>
            {slotName ? <span className="pair-half-slot">{slotName}</span> : null}
          </div>
        ) : slotName ? (
          <div className="pair-half-carrier">
            Crafted piece
            <span className="pair-half-slot">{slotName}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const pairSlotKey = (embellishment) => {
  if (!embellishment) return "none";
  return embellishment.bonus_id ? `bonus-${embellishment.bonus_id}` : `item-${embellishment.item_id}`;
};

// A doubled pair (same embellishment twice) is worn on two different pieces,
// so the second half takes the next distinct carrier slot the builds offer.
const resolvePairCarriers = (pair, builds) => {
  const firstCarriers = findEmbellishmentCarriers(builds, pair.embellishment_1?.embellishment_item_id);
  const secondCarriers = findEmbellishmentCarriers(builds, pair.embellishment_2?.embellishment_item_id);

  const firstCarrier = firstCarriers[0] || null;
  let secondCarrier = secondCarriers[0] || null;
  if (firstCarrier && secondCarrier && firstCarrier.slot === secondCarrier.slot) {
    secondCarrier = secondCarriers.find((carrier) => carrier.slot !== firstCarrier.slot) || secondCarrier;
  }

  return [firstCarrier, secondCarrier];
};

// The two embellishment slots are chosen together, so the backend publishes
// pairs (padded with null for "None") pre-sorted by popularity. Each half also
// shows the gear that commonly carries it, resolved from the recommended
// builds and gear tables.
const EmbellishmentPairs = ({ data, builds, slotItems }) => {
  return (
    <div className="wow-item-container wow-embellishment-pairs">
      <div className="wow-item-container-header">
        <div className="wow-item-container-header-title">Embellishment Combos</div>
        <div className="wow-item-container-header-title-metric-two">
          <div>Popularity</div>
          <TooltipContainer tooltipInfo="Popularity is how often this combination of embellishments is used by players in the top rankings of Raid or Mythic+ Dungeons.">
            <div className="tooltip-container">
              <TooltipIcon className="tooltip-icon" />
            </div>
          </TooltipContainer>
        </div>
      </div>
      {(data || []).map((pair) => {
        const [firstCarrier, secondCarrier] = resolvePairCarriers(pair, builds);
        return (
          <div className="pair-card" key={`${pairSlotKey(pair.embellishment_1)}_${pairSlotKey(pair.embellishment_2)}`}>
            <div className="pair-card-halves">
              <PairHalf embellishment={pair.embellishment_1} carrier={firstCarrier} slotItems={slotItems} />
              <PairHalf embellishment={pair.embellishment_2} carrier={secondCarrier} slotItems={slotItems} />
            </div>
            <div className="pair-card-metric">
              <div className="pair-card-percent">{Math.min(parseFloat(pair.percent), 100)}%</div>
              <div className="pair-card-count">({pair.count})</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EmbellishmentPairs;
